import {
	getMIPBankAccountData,
	updateMIPBankAccountData,
	updateMIPDraftGeneralDealInputs,
} from '@dealstack/frontend/shared/dataExchange/services/mip.service';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import API_PATHS from '@dealstack/shared/lib/generated/apiPaths';
import { useRequiredParams } from '@dealstack/frontend/shared/utils/useRequiredParams';
import { useTranslationDefault } from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks';
import { showToastV2 } from '@dealstack/frontend/shared/mechanism/toast/useToastV2';
import { useWrappedRequest } from '@dealstack/frontend/shared/other/react/ApiWrapper';
import { useCommonYupValidation } from '@dealstack/frontend/shared/other/react/form/useCommonYupValidation';
import { yupResolver } from '@hookform/resolvers/yup';
import { useContext, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import * as Yup from 'yup';

import { MIPCreateContext } from '@dealstack/frontend/app/pages/Project/MIP/MIPCreate/MIPCreateContext';
import { MIP_CREATE_ROUTES } from '@dealstack/frontend/app/pages/Project/MIP/MIPCreate/useMIPCreateController';
import {
	SAVE_FAILED,
	SAVE_INVALID,
	SAVE_OK,
	useWizardStep,
} from '@dealstack/frontend/shared/components/Wizard/WizardStepRegistry';
import type { WizardSaveResult } from '@dealstack/frontend/shared/components/Wizard/WizardStepRegistry.types';
import { DealContext } from '@dealstack/frontend/app/pages/Project/DealDashboard/DealContext';
import { useMipEntitySelectController } from '@dealstack/frontend/app/pages/Project/MIP/MIPCreate/GeneralDealInputs/useMipEntitySelectController.tsx';
import { CurrencyType } from '@dealstack/shared/lib/generated';

export enum GENERAL_DEAL_INPUTS_FORM {
	ENTITY_ID = 'entityId',
	PROGRAMME_NAME = 'PROGRAMME_NAME',
	INVESTOR_INSTRUCTIONS = 'investorInstructions',
	MIP_CURRENCY = 'currency',
}

export interface IMipCreateGeneralDealInputsValues {
	[GENERAL_DEAL_INPUTS_FORM.ENTITY_ID]: string | null;
	[GENERAL_DEAL_INPUTS_FORM.PROGRAMME_NAME]: string;
	[GENERAL_DEAL_INPUTS_FORM.INVESTOR_INSTRUCTIONS]: string;
	[GENERAL_DEAL_INPUTS_FORM.MIP_CURRENCY]: CurrencyType;
}

export const useGeneralDealInputsController = () => {
	const commonYup = useCommonYupValidation();
	const mipCreateController = useContext(MIPCreateContext);
	const { deal } = useContext(DealContext);
	const tDefault = useTranslationDefault();
	const { mipId } = useRequiredParams('mipId');
	const queryClient = useQueryClient();

	const { data: bankAccountDetails, isLoading: isBankAccountLoading } = useQuery({
		queryKey: [API_PATHS.MIP_BANK_ACCOUNT_GET, mipId],
		queryFn: () => getMIPBankAccountData(),
		select: (res) => (res.payload?.id ? res.payload : undefined),
	});

	const updateMIPDraftGeneralDealInputsRequest = useWrappedRequest(
		updateMIPDraftGeneralDealInputs
	);
	const updateMIPBankAccountRequest = useWrappedRequest(updateMIPBankAccountData);

	const mipData = mipCreateController?.getMipRequest?.response?.payload;

	const generalInputsBackupData = mipCreateController.dataBackup?.[MIP_CREATE_ROUTES.GENERAL];

	const generalDealInputsForm = useForm<IMipCreateGeneralDealInputsValues>({
		mode: 'onChange',
		defaultValues: {
			[GENERAL_DEAL_INPUTS_FORM.ENTITY_ID]: null,
			[GENERAL_DEAL_INPUTS_FORM.PROGRAMME_NAME]: '',
			[GENERAL_DEAL_INPUTS_FORM.INVESTOR_INSTRUCTIONS]: '',
			[GENERAL_DEAL_INPUTS_FORM.MIP_CURRENCY]: CurrencyType.EUR,
		},
		resolver: yupResolver(
			Yup.object().shape({
				[GENERAL_DEAL_INPUTS_FORM.PROGRAMME_NAME]: commonYup.inputNameString.required,
				[GENERAL_DEAL_INPUTS_FORM.ENTITY_ID]: commonYup.string.required,
			})
		),
	});

	useEffect(() => {
		generalDealInputsForm.setFocus(GENERAL_DEAL_INPUTS_FORM.PROGRAMME_NAME);
	}, []);

	const savedInvestorInstructions = bankAccountDetails?.investorInstructions;

	useEffect(() => {
		generalDealInputsForm.reset(
			{
				[GENERAL_DEAL_INPUTS_FORM.ENTITY_ID]:
					generalInputsBackupData?.entityId ?? mipData?.entity?.id ?? '',
				[GENERAL_DEAL_INPUTS_FORM.PROGRAMME_NAME]:
					generalInputsBackupData?.programmeName ?? mipData?.programmeName ?? '',
				[GENERAL_DEAL_INPUTS_FORM.INVESTOR_INSTRUCTIONS]:
					generalInputsBackupData?.investorInstructions ??
					savedInvestorInstructions ??
					'',
				[GENERAL_DEAL_INPUTS_FORM.MIP_CURRENCY]:
					(mipData?.currency || deal?.currency) ?? CurrencyType.EUR,
			},
			{ keepDirtyValues: true }
		);
	}, [mipData, deal, savedInvestorInstructions]);

	const { entityOptions, selectedEntity, onCreateNewEntity, isLoadingEntities, refetchEntities } =
		useMipEntitySelectController(mipCreateController.dealId as string, generalDealInputsForm);

	useEffect(() => {
		const entityCurrency = selectedEntity?.entityCurrency;
		if (!entityCurrency) {
			return;
		}
		generalDealInputsForm.setValue(GENERAL_DEAL_INPUTS_FORM.MIP_CURRENCY, entityCurrency);
	}, [selectedEntity]);

	const onSubmit = async (
		formData: IMipCreateGeneralDealInputsValues
	): Promise<WizardSaveResult> => {
		let result: WizardSaveResult = SAVE_OK;
		mipCreateController?.setIsLoading(true);
		let isAnyFailed = false;
		let isErrorToastShown = false;

		const shouldCreateNewEntity = selectedEntity?.isNew;

		const updateGeneralDealInputsPromise = await updateMIPDraftGeneralDealInputsRequest
			.fetch({
				entityNameToCreate: shouldCreateNewEntity ? selectedEntity.label : null,
				entityId: shouldCreateNewEntity ? null : formData.entityId,
				programmeName: formData[GENERAL_DEAL_INPUTS_FORM.PROGRAMME_NAME],
				currency: formData[GENERAL_DEAL_INPUTS_FORM.MIP_CURRENCY],
			})
			.onAnyError(() => {
				isAnyFailed = true;
			})
			.concludeWithPromise();

		const updatedMipData = updateGeneralDealInputsPromise?.payload;
		if (!updatedMipData?.id) {
			showToastV2({
				title: tDefault('common.toast.error.message'),
				state: 'error',
			});
			mipCreateController?.setIsLoading(false);
			return SAVE_INVALID;
		}

		const updateBankAccountPromise = bankAccountDetails?.id
			? updateMIPBankAccountRequest
					.fetch({
						params: { bankAccountId: bankAccountDetails.id },
						body: {
							investorInstructions:
								formData[GENERAL_DEAL_INPUTS_FORM.INVESTOR_INSTRUCTIONS],
						},
					})
					.onAnyError(() => {
						isAnyFailed = true;
						isErrorToastShown = true;
					})
					.concludeWithPromise()
			: undefined;

		await Promise.allSettled([updateGeneralDealInputsPromise, updateBankAccountPromise]).then(
			() => {
				if (isAnyFailed) {
					mipCreateController?.updateBackupData(MIP_CREATE_ROUTES.GENERAL, {
						entityId: formData[GENERAL_DEAL_INPUTS_FORM.ENTITY_ID],
						investorInstructions:
							formData[GENERAL_DEAL_INPUTS_FORM.INVESTOR_INSTRUCTIONS],
					});
					if (!isErrorToastShown) {
						showToastV2({
							title: tDefault('common.toast.error.message'),
							state: 'error',
						});
					}
					result = SAVE_FAILED;
				} else {
					mipCreateController?.removeBackupData(MIP_CREATE_ROUTES.GENERAL);
				}
			}
		);

		await Promise.all([
			queryClient.invalidateQueries({ queryKey: [API_PATHS.MIP_BANK_ACCOUNT_GET] }),
			mipCreateController?.getMipRequest?.fetch().concludeWithPromise(),
			refetchEntities(),
		]);
		mipCreateController?.setIsLoading(false);
		return result;
	};

	useWizardStep(() => onSubmit(generalDealInputsForm.getValues()));

	return {
		generalDealInputsForm,
		onSubmit,
		bankAccountDetails,
		isLoading:
			isBankAccountLoading ||
			mipCreateController.getMipRequest.isLoading ||
			isLoadingEntities,
		entityOptions,
		onCreateNewEntity,
		disableCurrencySelect: selectedEntity && !selectedEntity.isNew,
	};
};
